import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { currentMonth, fetchDetailExport, fetchDetails, fetchExport, fetchReport } from "../service";
import type { PerformanceDetailFilters, PerformanceDetailSelection, PerformanceMetric } from "../types";

/** 下载完成后释放本次文件对象地址。 */
function saveFile(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** 编排查询月份与输入月份；导出始终使用当前已加载结果的月份。 */
export function useSalesPerformanceRuntime(canExport: boolean, initialMonth = currentMonth()) {
  const [month, setMonth] = useState(initialMonth);
  const [draftMonth, setDraftMonth] = useState(initialMonth);
  const [detailSelection, setDetailSelection] = useState<PerformanceDetailSelection | null>(null);
  const [detailsPage, setDetailsPage] = useState(1);
  const [detailsPageSize, setDetailsPageSize] = useState(20);
  const query = useQuery({
    queryKey: ["sales-performance", month],
    queryFn: ({ signal }) => fetchReport(month, signal),
    retry: false,
    refetchOnWindowFocus: false,
  });
  const download = useMutation({
    mutationFn: async (reportMonth: string) => {
      const blob = await fetchExport(reportMonth);
      saveFile(blob, `销售部绩效_${reportMonth}.xlsx`);
    },
  });
  const detailFilters: PerformanceDetailFilters | null = detailSelection && {
    month: detailSelection.month, user_id: detailSelection.user_id, metric: detailSelection.metric,
  };
  const detailsQuery = useQuery({
    queryKey: ["sales-performance-details", detailFilters, detailsPage, detailsPageSize],
    queryFn: ({ signal }) => {
      if (!detailFilters) throw new Error("请先选择绩效数字");
      return fetchDetails(detailFilters, detailsPage, detailsPageSize, signal);
    },
    enabled: Boolean(detailFilters), retry: false, refetchOnWindowFocus: false,
  });
  const detailDownload = useMutation({
    mutationFn: async (filters: PerformanceDetailFilters) => {
      saveFile(await fetchDetailExport(filters), `销售部绩效明细_${filters.month}_${filters.user_id}_${filters.metric}.xlsx`);
    },
  });
  return {
    month, draftMonth, setDraftMonth, query,
    detailSelection, detailsPage, detailsPageSize, detailsQuery, setDetailsPage,
    isDetailsExporting: detailDownload.isPending, detailsExportError: detailDownload.error,
    openDetails(userId: number, userName: string, metric: PerformanceMetric, metricLabel: string) {
      if (!query.data || query.isFetching || query.isError || detailDownload.isPending) return;
      detailDownload.reset();
      setDetailsPage(1);
      setDetailSelection({ month: query.data.month, user_id: userId, user_name: userName, metric, metric_label: metricLabel });
    },
    closeDetails() { setDetailSelection(null); detailDownload.reset(); },
    changeDetailsPageSize(value: number) { setDetailsPage(1); setDetailsPageSize(value); },
    exportDetails() {
      if (!canExport || !detailFilters || !detailsQuery.data || detailsQuery.isFetching ||
          detailsQuery.isError || detailDownload.isPending) return;
      detailDownload.mutate(detailFilters);
    },
    isExporting: download.isPending,
    exportError: download.error,
    search() {
      if (!draftMonth || download.isPending) return;
      download.reset();
      setDetailSelection(null);
      if (draftMonth === month) void query.refetch();
      else setMonth(draftMonth);
    },
    exportFile() {
      if (!canExport || !query.data || query.isFetching || query.isError || download.isPending) return;
      download.mutate(query.data.month);
    },
  };
}
